import { ModelRef, ReviewCapabilityFields, ReviewCheckSpec } from '../../../api/structs/pipeline';

/**
 * The engine's `MaxReviewSceneScope`. 1 and 2 are literal neighbour counts; 3
 * is "the story so far" — a budgeted selection of earlier scenes rather than
 * three of them.
 */
export const MAX_REVIEW_SCENE_SCOPE = 3;

/**
 * What a layer of the cascade actually resolves to: every capability with a
 * value, never `undefined`. The form shows one of these as the "inherits: …"
 * caption under an unset control.
 */
export interface ResolvedReviewCapabilities {
  story: boolean;
  scene_scope: number;
  descriptions: boolean;
  attempt_history: boolean;
  cast: boolean;
  pair_judge: boolean;
  /** `{}` when no layer named a pair-judge model — it then follows confirm. */
  pair: ModelRef;
  fixed_checks: ReviewCheckSpec[];
  lenses: string[];
  guidance: string;
}

/**
 * What a run assumes when NOTHING is configured anywhere — the engine's seeded
 * `reviewSettings` before the first layer speaks. Everything off, no scope, no
 * lists, no guidance.
 */
export const HARD_REVIEW_CAPABILITIES: ResolvedReviewCapabilities = {
  story: false,
  scene_scope: 0,
  descriptions: false,
  attempt_history: false,
  cast: false,
  pair_judge: false,
  pair: {},
  fixed_checks: [],
  lenses: [],
  guidance: '',
};

const clampSceneScope = (scope: number): number => {
  if (!Number.isFinite(scope) || scope < 0) return 0;
  if (scope > MAX_REVIEW_SCENE_SCOPE) return MAX_REVIEW_SCENE_SCOPE;
  return Math.trunc(scope);
};

/** Trim + lowercase, the client mirror of `types.NormalizeReviewLens`. */
const normalizeLens = (lens: string): string => String(lens ?? '').trim().toLowerCase();

const applyModelRef = (into: ModelRef, from?: ModelRef): ModelRef => {
  if (!from) return into;
  const next: ModelRef = { ...into };
  const provider = (from.provider || '').trim();
  const model = (from.model || '').trim();
  if (provider) next.provider = provider;
  if (model) next.model = model;
  return next;
};

/**
 * The client-side mirror of `applyReviewCapabilities`
 * (`internal/pipeline/review_settings_capabilities.go`), applied over the hard
 * defaults in cascade order — account, then `review_defaults`, then a
 * checkpoint's `review`.
 *
 * Two rules, and the difference between them is the whole design:
 *
 *  - the scalar fields (story, scene_scope, descriptions, attempt_history,
 *    cast, pair_judge, and each half of pair) override whenever the layer NAMED
 *    them. `undefined` is "said nothing"; `false` and `0` are real choices, so
 *    a checkpoint can narrow a template that asked for more;
 *  - the lists and the guidance string REPLACE when non-empty, never merge —
 *    a layer that could only add would be unable to trim four lenses down to
 *    the one that matters here.
 *
 * It exists ONLY to label a control with what it would inherit. It is
 * deliberately a mirror rather than a fetch: the value has to update as the
 * user types, before anything is saved. When it and the engine disagree the
 * engine is right and this is a bug.
 */
export const resolveReviewCapabilities = (
  ...layers: (ReviewCapabilityFields | undefined | null)[]
): ResolvedReviewCapabilities => {
  const resolved: ResolvedReviewCapabilities = {
    ...HARD_REVIEW_CAPABILITIES,
    pair: {},
    fixed_checks: [],
    lenses: [],
  };

  for (const layer of layers) {
    if (!layer) continue;
    if (typeof layer.story === 'boolean') resolved.story = layer.story;
    if (typeof layer.scene_scope === 'number') resolved.scene_scope = layer.scene_scope;
    if (typeof layer.descriptions === 'boolean') resolved.descriptions = layer.descriptions;
    if (typeof layer.attempt_history === 'boolean') resolved.attempt_history = layer.attempt_history;
    if (typeof layer.cast === 'boolean') resolved.cast = layer.cast;
    if (typeof layer.pair_judge === 'boolean') resolved.pair_judge = layer.pair_judge;
    resolved.pair = applyModelRef(resolved.pair, layer.pair);

    if (layer.fixed_checks && layer.fixed_checks.length > 0) {
      resolved.fixed_checks = layer.fixed_checks.map((spec) => ({
        ...spec,
        check: (spec.check || '').trim(),
      }));
    }
    if (layer.lenses && layer.lenses.length > 0) {
      resolved.lenses = (layer.lenses as string[]).map(normalizeLens);
    }
    const guidance = (layer.guidance || '').trim();
    if (guidance) resolved.guidance = guidance;
  }

  // The engine's run-time backstop: a template stored before the save-time rule
  // existed must not make the caption promise a scope the judge cannot render.
  resolved.scene_scope = clampSceneScope(resolved.scene_scope);
  return resolved;
};

export default resolveReviewCapabilities;
